import { Injectable } from '@angular/core';
import { McticHttpClient, SidenavService } from '@mctic/ng-core';
import { Observable } from 'rxjs';
import { map, publishReplay, refCount } from 'rxjs/operators';
import { AnoBase, ComposicaoRda, EmpresaVO, Instituicao, RdaAutenticado, SelecionaEntidadeVO, UsuarioAutenticado } from '../shared/model';
import { UsuarioSessao } from '../shared/model/usuario-sessao.model';
import { PerfilCorporativoService } from './perfil-corporativo.service';
import { SigplaniModuloEnum } from '../shared/enum/sigplani-modulo.enum';

@Injectable({
  providedIn: 'root'
})
export class SecurityService {


  private readonly xAuthModel = 'x-auth-model';
  private readonly resource = '/sepin-backend/rest/security';

  private _usuarioAutenticado: Observable<UsuarioAutenticado>;
  private _rdaAutenticado: Observable<RdaAutenticado>;

  constructor(private readonly http: McticHttpClient,
    private readonly sidenavService: SidenavService,
    private readonly perfilCorporativoService: PerfilCorporativoService) {
  }

  obterUsuarioAutenticado(): Observable<UsuarioAutenticado> {
    if (!this._usuarioAutenticado) {
      this._usuarioAutenticado = this.http
        .get<UsuarioAutenticado>(this.resource)
        .pipe(publishReplay(1), refCount());
    }

    return this._usuarioAutenticado;
  }

  obterRdaAutenticado(): Observable<RdaAutenticado> {
    if (!this._rdaAutenticado) {
      this._rdaAutenticado = this.http
        .get<RdaAutenticado>('/sepin-backend/rest/security/rda')
        .pipe(publishReplay(1), refCount());
    }

    return this._rdaAutenticado;
  }

  atualizarAnoBaseAutenticado(anoSelecionado: AnoBase) {
    const usuarioSessao = this.usuarioSessao;
    usuarioSessao.anoBase = anoSelecionado;
    this.atualizarToken(usuarioSessao);
  }

  get usuarioSessao(): UsuarioSessao {
    const authB64 = sessionStorage.getItem(this.xAuthModel);

    if (!authB64) {
      return null;
    }

    const authString = atob(authB64);

    if (!authString) {
      return null;
    }

    const auth: UsuarioSessao = JSON.parse(authString);

    if (auth) {
      return auth;
    }
  }

  limparCacheAutenticado() {
    this._usuarioAutenticado = null;
    this._rdaAutenticado = null;
  }

  limparCacheTokenRda() {
    const sessao: UsuarioSessao = this.usuarioSessao;
    sessao.empresa = null;
    sessao.anoBase = null;
    sessao.entidadeSelecionada = null;
    sessao.rdaAutenticado = null;
    sessao.instituicao = null;
    this.limparCacheAutenticado();
    sessionStorage.setItem(this.xAuthModel, btoa(JSON.stringify(sessao)));
  }

  recarregarRdaAutenticado() {
    this.removerRdaAutenticadoToken(this.usuarioSessao);
    this.obterRdaAutenticado().subscribe((rdaAutenticado: RdaAutenticado) => {
      this.atualizarRdaAutenticadoToken(rdaAutenticado);
    });
  }

  private removerRdaAutenticadoToken(sessao: UsuarioSessao) {
    this._rdaAutenticado = null;
    sessao.rdaAutenticado = null;
    sessionStorage.setItem(this.xAuthModel, btoa(JSON.stringify(sessao)));
  }

  atualizarRdaAutenticadoToken(rdaAutenticado: RdaAutenticado) {
    const usuarioSessao = this.usuarioSessao;
    usuarioSessao.rdaAutenticado = rdaAutenticado;
    this.atualizarToken(usuarioSessao);
  }

  atualiazarEmpresaAutenticadoToken(empresaVO: EmpresaVO) {
    const sessao = this.usuarioSessao;
    sessao.empresa = empresaVO;
    this.atualizarToken(sessao);
  }

  atualiazarInstituicaoAutenticadaToken(instituicao: Instituicao) {
    const sessao = this.usuarioSessao;
    sessao.instituicao = instituicao;
    this.limparRdaAutenticado(sessao);
    this.atualizarToken(sessao);
  }

  limparRdaAutenticado(sessao: UsuarioSessao) {
    sessao.rdaAutenticado = null;
    this._rdaAutenticado = null;
    this.atualizarToken(sessao);
  }

  atualizarToken(sessao: UsuarioSessao) {
    sessionStorage.removeItem(this.xAuthModel);
    sessionStorage.setItem(this.xAuthModel, btoa(JSON.stringify(sessao)));
  }

  atualizarUsuarioAutenticadoToken(usuarioAutenticado: UsuarioAutenticado) {
    let usuarioSessao = this.usuarioSessao;
    if (usuarioSessao === null) {
      usuarioSessao = new UsuarioSessao();
    }
    usuarioSessao.usuario = usuarioAutenticado;
    sessionStorage.setItem(this.xAuthModel, btoa(JSON.stringify(usuarioSessao)));
  }


  atualizarEntidadeSelecionada(entidadeSelecionada: SelecionaEntidadeVO) {
    const usuarioSessao = this.usuarioSessao;
    usuarioSessao.entidadeSelecionada = entidadeSelecionada;
    sessionStorage.setItem(this.xAuthModel, btoa(JSON.stringify(usuarioSessao)));
  }

  limparSelecionadas() {
    sessionStorage.removeItem(this.xAuthModel);
    this._rdaAutenticado = null;
    this._usuarioAutenticado = null;
  }

  isEmpresaAutenticada(): boolean {
    const usuarioSessao = this.usuarioSessao;
    return !!usuarioSessao.empresa;
  }

  isInstituicaoAutenticada(): boolean {
    const usuarioSessao = this.usuarioSessao;
    return !!usuarioSessao.instituicao;
  }

  isEntidadeSelecionada(): boolean {
    const usuarioSessao = this.usuarioSessao;
    return !!usuarioSessao.empresa || !!usuarioSessao.instituicao;
  }

  alternarModulo(modulo: SigplaniModuloEnum): void {
    const usuarioSessao = this.usuarioSessao;
    usuarioSessao.modulo = modulo;
    usuarioSessao.gerenciamento = modulo === SigplaniModuloEnum.GERENCIAMENTO;
    sessionStorage.setItem(this.xAuthModel, btoa(JSON.stringify(usuarioSessao)));
  }

  isModuloAtivo(modulo: SigplaniModuloEnum): boolean {
    return this.usuarioSessao.modulo === modulo;
  }

}
